import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const SettingsContext = createContext(null);

function hexToHsl(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  const [r, g, b] = m.slice(1).map((x) => parseInt(x, 16) / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);

  const reload = useCallback(() => api.get("/settings").then((r) => setSettings(r.data)), []);
  useEffect(() => { reload(); }, [reload]);

  const accent = settings?.accent_color;
  useEffect(() => {
    const hsl = hexToHsl(accent);
    const root = document.documentElement.style;
    if (hsl) {
      root.setProperty("--primary", hsl);
      root.setProperty("--ring", hsl);
    } else {
      root.removeProperty("--primary");
      root.removeProperty("--ring");
    }
  }, [accent]);

  const value = useMemo(() => ({ settings, setSettings, reload }), [settings, reload]);
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => useContext(SettingsContext);
